import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Popover, Space, DatePicker, Form, Row, Col, Radio } from 'antd';
import {
  map as _map,
  isEmpty as _isEmpty,
  isEqual as _isEqual,
  delay as _delay,
  filter as _filter,
  cloneDeep as _cloneDeep,
  isNull as _isNull,
  isUndefined as _isUndefined
} from 'lodash-es';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Icon from 'components/Icon';
import { useNavigate} from 'react-router-dom';
import './GraasDateRangePicker.scss';

const { RangePicker } = DatePicker;

const GraasDateRangePicker = props => {
  const {
    selectedDateRange,
    customDateRanges,
    isDisableFutureDate,
    restrictDateRange = false,
    availableDateRange,
    refreshAvailableDateRange,
    defaultDateRange,
    onUpdateDate,
    showApplyAllButton = false,
    closeOnScroll = false,
    isSaveButton = false,
    type,
    extraStyle,
    customLabel,
    isSpecialEventNeeded = true,
    placement = 'bottomLeft',
    customDisableDate = false,
    customDisableDateRange,
    isDefaultDateRangeApplicable = true,
    isTestUser = false,
    ...rest
  } = props;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  let lastSevenDays = isDefaultDateRangeApplicable ? [moment().subtract(7, 'd'), moment()] : [];

  // ** States
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [dateRangeDuration, setDateRangeDuration] = useState('custom');
  const [previousDateDuration, setPreviousDateDuration] = useState('custom');
  const [dateRange, setDateRange] = useState(lastSevenDays);
  const [confirmedDateRange, setConfirmedDateRange] = useState(lastSevenDays);
  const calendarEvents = useSelector(state => state.Account.calendarEvents?.calendarEvents) || [];
  const [activeCalendarEvents, setActiveCalendarEvents] = useState([]);

  const customDisabledDate = current => {
    // Disable all future dates
    if (current && current.isAfter(moment())) {
      return true;
    }

    if (isTestUser) {
      const threeMonthsAgo = moment().subtract(3, 'months').startOf('day');
      if (current.isBefore(threeMonthsAgo)) {
        return true;
      }
    }
    // Disable dates that are more than 30 days before or after the selected range
    if (dateRange && dateRange.length > 0) {
      const tooLate = dateRange[0] && current.diff(dateRange[0], 'days') > customDisableDateRange;
      const tooEarly = dateRange[1] && dateRange[1].diff(current, 'days') > customDisableDateRange;
      return !!tooEarly || !!tooLate;
    }

    return false;
  };

  useEffect(() => {
    if (!_isEmpty(calendarEvents)) {
      const events = _cloneDeep(calendarEvents);
      const activeEventList = _filter(events, item => item?.isEventEnabled) || [];
      if (!_isEmpty(activeEventList)) {
        setActiveCalendarEvents(activeEventList);
      }
    }
  }, [calendarEvents]);

  useEffect(() => {
    if (type && !_isEmpty(type)) {
      setDateRangeDuration(type);
    }
  }, [type]);

  // - update dates in state initially
  const updateDefaultDates = useCallback(date => {
    setDateRange(date);
    setConfirmedDateRange(date);
  }, []);

  useEffect(() => {
    if (restrictDateRange && availableDateRange) {
      if (!availableDateRange?.startDate?.isAfter(confirmedDateRange[0])) {
        form.setFieldValue('dateRange', confirmedDateRange);
        updateDefaultDates(confirmedDateRange);
      } else {
        form.setFieldValue('dateRange', [availableDateRange.startDate, availableDateRange.endDate]);
        updateDefaultDates([availableDateRange.startDate, availableDateRange.endDate]);
      }
    } else {
      form.setFieldValue('dateRange', selectedDateRange || lastSevenDays);
      updateDefaultDates(selectedDateRange || lastSevenDays);
    }
  }, [restrictDateRange, availableDateRange, selectedDateRange, updateDefaultDates]);

  const handlePopoverClose = () => {
    setIsPopoverVisible(false);
  };

  useEffect(() => {
    if (closeOnScroll) {
      document.addEventListener('scroll', handlePopoverClose, true);
    }
  }, []);

  // ** Default date ranges
  const currentDefaultRanges = {
    [t('GRAAS_TODAY_GRAAS')]: [moment(), moment()],
    [t('GRAAS_YESTERDAY_GRAAS')]: [moment().subtract(1, 'd'), moment().subtract(1, 'd')],
    [t('GRAAS_THIS_WEEK_GRAAS')]: [moment().startOf('week'), moment()],
    [t('GRAAS_LAST_WEEK_GRAAS')]: [
      moment().startOf('week').subtract(1, 'week'),
      moment().endOf('week').subtract(1, 'week')
    ],
    [t('GRAAS_LAST_7_DAYS_GRAAS')]: [moment().subtract(6, 'd'), moment()],
    [t('GRAAS_LAST_14_DAYS_GRAAS')]: [moment().subtract(13, 'd'), moment()],
    // [t('GRAAS_LAST_28_DAYS_GRAAS')]: [moment().subtract(27, 'd'), moment()],
    [t('GRAAS_THIS_MONTH_GRAAS')]: [moment().startOf('month'), moment()],
    [t('GRAAS_LAST_MONTH_GRAAS')]: [
      moment().startOf('month').subtract(1, 'months'),
      moment().subtract(1, 'months').endOf('month')
    ],
    [t('GRAAS_THIS_QUARTER_GRAAS')]: [moment().startOf('quarter'), moment()],
    ...(isTestUser ? {} : {
      [t('GRAAS_LAST_QUARTER_GRAAS')]: [
        moment().startOf('quarter').subtract(1, 'quarters'),
        moment().endOf('quarter').subtract(1, 'quarters')
      ],
      [t('GRAAS_THIS_YEAR_GRAAS')]: [moment().startOf('year'), moment()],
      [t('GRAAS_LAST_YEAR_GRAAS')]: [
        moment().startOf('year').subtract(1, 'years'),
        moment().endOf('year').subtract(1, 'years')
      ]
    }),
    //TC-12653 - We have standardized the date range format across all pages.
    // ...(!_isEmpty(customDateRanges) && { ...customDateRanges })
  };

  // ** Duration Types [day, week, month, year]
  const config = [
    {
      key: 'date',
      label: t('GRAAS_BY_DAY_GRAAS')
    },
    {
      key: 'week',
      label: t('GRAAS_BY_WEEK_GRAAS')
    },
    {
      key: 'month',
      label: t('GRAAS_BY_MONTH_GRAAS')
    },
    {
      key: 'year',
      label: t('GRAAS_BY_YEAR_GRAAS')
    },
    {
      key: 'custom',
      label: t('GRAAS_CUSTOM_GRAAS')
    }
  ];

  if (isSpecialEventNeeded) {
    config?.push({
      key: 'specialEvents',
      label: t('GRAAS_SPECIAL_EVENTS_GRAAS')
    });
  }

  // ** Reset on cancel
  const onResetForm = () => {
    setIsPopoverVisible(false);
    _delay(() => {
      setDateRangeDuration('custom');
      setDateRange(selectedDateRange || lastSevenDays);
      setConfirmedDateRange(selectedDateRange || lastSevenDays);
      form.resetFields(['dateRange', 'datePicker']);
    }, 400);
  };

  // ** Render Picker inside popover
  const renderPicker = type => {
    // ** Range picker props
    const rangePickerProps = {
      ...rest,
      popupStyle: { padding: '0' },
      open: true,
      ranges: !restrictDateRange
        ? currentDefaultRanges
        : Object.fromEntries(
            Object.entries(currentDefaultRanges).filter(([key, [rangeStart, rangeEnd]]) => {
              return (
                rangeStart.isBetween(availableDateRange.startDate, availableDateRange.endDate, null, '[]') &&
                rangeEnd.isBetween(availableDateRange.startDate, availableDateRange.endDate, null, '[]')
              );
            })
          ),
      ...(!customDisableDate &&
        isDisableFutureDate && { disabledDate: current => (current ? current.isAfter(moment()) : false) }),
      ...(!customDisableDate &&
        restrictDateRange && {
          disabledDate: current =>
            !current.isBetween(availableDateRange.startDate, availableDateRange.endDate, null, '[]')
        }),
      getPopupContainer: node => node.parentNode,
      onCalendarChange: (dates, dateStrings, event) => onChangeDate(dates, dateStrings, dateRangeDuration, event),
      ...(customDisableDate && { disabledDate: customDisabledDate })
    };
    // ** day, week, month, year picker props
    const datePickerProps = {
      ...rest,
      open: true,
      showToday: false,
      picker: dateRangeDuration,
      getPopupContainer: node => node.parentNode,
      onChange: (dates, dateStrings) => onChangeDate(dates, dateStrings, dateRangeDuration),
      disabledDate: current => {
        if (isTestUser && dateRangeDuration === 'year') {
          // Only enable current year for test users in year picker
          const currentYear = moment().year();
          return current.year() !== currentYear;
        }
        if (isTestUser && dateRangeDuration === 'month') {
          // Only enable last 3 months (including current month)
          const startMonth = moment().subtract(3, 'months').startOf('month');
          const endMonth = moment().endOf('month');
          return current < startMonth || current > endMonth;
        }
        if (isTestUser && (dateRangeDuration === 'date' || dateRangeDuration === 'week')) {
          // Only enable last 3 months (including today)
          const startDay = moment().subtract(3, 'months').startOf('day');
          const endDay = moment().endOf('day');
          return current < startDay || current > endDay;
        }
        // Default: disable future dates
        return current && current > moment().endOf('day');
      },
      ...(restrictDateRange && {
        disabledDate: current => {
          if (isTestUser && dateRangeDuration === 'year') {
            const currentYear = moment().year();
            return current.year() !== currentYear;
          }
          if (isTestUser && dateRangeDuration === 'month') {
            const startMonth = moment().subtract(3, 'months').startOf('month');
            const endMonth = moment().endOf('month');
            return current < startMonth || current > endMonth;
          }
          if (isTestUser && (dateRangeDuration === 'date' || dateRangeDuration === 'week')) {
            const startDay = moment().subtract(3, 'months').startOf('day');
            const endDay = moment().endOf('day');
            return current < startDay || current > endDay;
          }
          return !current.isBetween(availableDateRange.startDate, availableDateRange.endDate, null, '[]');
        }
      })
    };

    if (!isSpecialEventNeeded) {
      datePickerProps.disabledDate = false;
    }

    const applyToAll = () => {
      let rangeToSend = dateRange;
      if (isTestUser && (dateRangeDuration === 'year' || dateRangeDuration === 'month')) {
        rangeToSend = [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')];
      }
      setConfirmedDateRange(rangeToSend);
      setIsPopoverVisible(false);
      onUpdateDate(rangeToSend, true);
    };

    const getEventOptions = () => {
      return _map(activeCalendarEvents, item => {
        const startDate = moment(new Date(item?.startTime * 1000));
        const endDate = moment(new Date(item?.endTime * 1000));
        const threeMonthsAgo = moment().subtract(3, 'months').startOf('day');
        
        const isDisabled = isTestUser && endDate.isBefore(threeMonthsAgo);
        
        const renderRadioContent = (
          <div>
            <span className='c-heading smm' style={{ wordBreak: 'break-word' }}>
              {item?.title}
            </span>
            <span className='c-heading color-light sm'>{`${moment(new Date(item?.startTime * 1000)).format(
              'DD/MM/YYYY'
            )} -  ${moment(new Date(item?.endTime * 1000)).format('DD/MM/YYYY')}`}</span>
          </div>
        );
        return (
          <>
            <Radio value={item?.title} disabled={isDisabled}>{renderRadioContent}</Radio>
            <br />
          </>
        );
      });
    };

    const handledOnClick = item => {
      if (!['custom', 'specialEvents']?.includes(previousDateDuration)) {
        if (previousDateDuration !== item?.key) {
          form.resetFields(['datePicker']);
        } else {
          form.setFieldValue('datePicker', moment(dateRange[0]));
        }
      }
      setDateRangeDuration(item?.key);
    };

    const renderSpecialEventsContent = () => {
      const specialEventsTitle = `${t('GRAAS_SPECIAL_EVENTS_GRAAS')} (${activeCalendarEvents?.length})`;

      return (
        <div className='special-events-container height-auto'>
          <Row justify='space-between' md={24}>
            {/* Special Event Heading */}
            <Col md={16}>
              <div className='c-heading md color-dark m-2'>{specialEventsTitle}</div>
            </Col>
            {/* Special Event Button */}
            <Col md={8}>{!_isEmpty(activeCalendarEvents) && renderManageSpecialEventsButton(false)}</Col>
          </Row>
          {/* Special Event Message and Button */}
          {_isEmpty(activeCalendarEvents) ? (
            <Row justify='center' className='special-events-button-row'>
              {renderManageSpecialEventsButton(true)}
            </Row>
          ) : (
            <Row justify='' style={{ height: 300, overflowY: 'scroll', scrollbarWidth: 'thin' }}>
              <Radio.Group
                className='customRadioGroup m-2'
                onChange={e => {
                  const val = _filter(activeCalendarEvents, item => item?.title === e.target.value)?.[0] || [];
                  onChangeDate([moment(new Date(val?.startTime * 1000)), moment(new Date(val?.endTime * 1000))]);
                }}
              >
                {getEventOptions()}
              </Radio.Group>
            </Row>
          )}
        </div>
      );
    };

    const renderManageSpecialEventsButton = isInfoNeeded => {
      return (
        <div className='m-2'>
          {isInfoNeeded && (
            <label
              className='c-heading sm color-dark'
              dangerouslySetInnerHTML={{ __html: t('GRAAS_MANAGE_SPECIAL_EVENTS_MSG_GRAAS') }}
            />
          )}
          <button
            className='c-button is-link sm no-pad'
            onClick={() => {
              navigate(`/profile/business-profile?type=special_events`);
            }}
            style={{ display: 'flex', justifyContent: 'space-between', marginLeft: isInfoNeeded ? -25 : -10 }}
          >
            <div className={isInfoNeeded ? 'm-2' : ''} style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name='settings' isBordered />
              <span style={{ marginLeft: 8 }}>{t('GRAAS_MANAGE_SPECIAL_EVENTS_GRAAS')}</span>
            </div>
          </button>
        </div>
      );
    };

    const findDaterangeCompage = (dateRange, confirmedDateRange) => {
      if (_isNull(dateRange) || _isUndefined(dateRange[0])) return true;

      if (dateRange[0]?.toString() === 'Invalid date' || dateRange[1]?.toString() === 'Invalid date') return true;
      return _isEqual(dateRange, confirmedDateRange);
    };

    return (
      <div className='pickerContainer'>
        <div className='pickerContainer_left'>
          <ul>
            {_map(config, item => {
              return (
                <li key={item?.key}>
                  <button className={item?.key === type ? 'active' : ''} onClick={() => handledOnClick(item)}>
                    <Space size={1}>
                      <Icon name='chevron_right' size='small' isBordered />
                      {item?.label}
                    </Space>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className='pickerContainer_right'>
          <Form
            form={form}
            onFinish={() => {
              let rangeToSend = dateRange;
              if (isTestUser && (dateRangeDuration === 'year' || dateRangeDuration === 'month')) {
                rangeToSend = [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')];
              }
              setConfirmedDateRange(rangeToSend);
              setIsPopoverVisible(false);
              setPreviousDateDuration(dateRangeDuration);
              onUpdateDate(rangeToSend, false, dateRangeDuration);
            }}
            className='rangePickerForm'
            initialValues={!_isNull(dateRange) ? { dateRange: [moment(dateRange[0]), moment(dateRange[1])] } : {}}
          >
            {dateRangeDuration === 'custom' ? (
              <Form.Item name='dateRange' className='mb-0'>
                <RangePicker {...rangePickerProps} />
              </Form.Item>
            ) : dateRangeDuration === 'specialEvents' ? (
              isSpecialEventNeeded && renderSpecialEventsContent()
            ) : (
              <Form.Item name='datePicker' className='mb-0'>
                <DatePicker {...datePickerProps} />
              </Form.Item>
            )}
            {restrictDateRange && (
              <span className='c-heading sm restrictionReason'>{t('GRAAS_DATE_RANGE_RESTRICTION_REASON_GRAAS')}</span>
            )}
            <div className='pickerButtonContainer'>
              <Space>
                <button className='c-button is-bordered' type='button' onClick={() => onResetForm()}>
                  {t('GRAAS_CANCEL_GRAAS')}
                </button>
                <button
                  className='c-button is-filled'
                  type='submit'
                  disabled={findDaterangeCompage(dateRange, confirmedDateRange)}
                >
                  {isSaveButton ? t('GRAAS_SAVE_GRAAS') : t('GRAAS_APPLY_GRAAS')}
                </button>
                {showApplyAllButton && (
                  <button
                    className='c-button is-filled'
                    onClick={() => applyToAll()}
                    disabled={findDaterangeCompage(dateRange, confirmedDateRange)}
                  >
                    {t('GRAAS_APPLY_TO_ALL_GRAAS')}
                  </button>
                )}
              </Space>
            </div>
          </Form>
        </div>
      </div>
    );
  };

  // ** on change date
  const onChangeDate = useCallback((dates, dateStrings, type, event) => {
    if (_isNull(dates)) {
      setDateRange(null);
    } else {
      let selectedDateRange = [moment(dates[0]), moment(dates[1])];

      // ** If Day is selected
      if (type === 'date') {
        selectedDateRange = [moment(dates), moment(dates)];
      }

      // ** If Week is selected
      if (type === 'week') {
        const split = dateStrings.split('-');
        const startDayOfWeek = moment()
          .year(+split[0])
          .week(+split[1].substring(0, split[1].length - 2))
          .startOf('week');
        let endDayOfWeek = moment()
          .year(+split[0])
          .week(+split[1].substring(0, split[1].length - 2))
          .endOf('week');

        const today = moment();
        if (isDisableFutureDate && endDayOfWeek?.isAfter(today)) {
          endDayOfWeek = today;
        }
        selectedDateRange = [startDayOfWeek, endDayOfWeek];
      }

      // ** If Month is selected
      if (type === 'month') {
        const split = dateStrings.split('-');
        let startDayOfMonth = moment()
          .month(+split[1] - 1)
          .year(split[0])
          .startOf('month');
        let endDayOfMonth = moment()
          .month(+split[1] - 1)
          .year(split[0])
          .endOf('month');

        const today = moment();
        if (isDisableFutureDate && endDayOfMonth?.isAfter(today)) {
          endDayOfMonth = today;
        }
        selectedDateRange = [startDayOfMonth, endDayOfMonth];
      }

      // ** If Year is selected
      if (type === 'year') {
        const startDayOfYear = moment().year(dateStrings).startOf('year');
        let endDayOfYear = moment().year(dateStrings).endOf('year');
        const today = moment();

        if (isDisableFutureDate && endDayOfYear?.isAfter(today)) {
          endDayOfYear = today;
        }

        selectedDateRange = [startDayOfYear, endDayOfYear];
      }

      if (type === 'custom') {
        const [startDate, endDate] = dates;
        if (!startDate && endDate) {
          selectedDateRange = [moment(dates[1]), moment('')];
        }
      }

      // If test user and year/month, override to last 3 months
      if (isTestUser && (type === 'year' || type === 'month')) {
        selectedDateRange = [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')];
      }

      if (!_isEmpty(selectedDateRange)) setDateRange(selectedDateRange);

      if (type === 'custom') {
        // ** Focus on the start & end Date input field if the user selects a custom range
        const [startDate, endDate] = dates;
        const startInput = document.querySelector('input[placeholder="Start date"]');
        const endInput = document.querySelector('input[placeholder="End date"]');
        if (!startDate && endDate && endInput) {
          form.setFieldValue('dateRange', [moment(dates[1]), null]);
          _delay(() => {
            endInput.focus();
          }, 10);
        } else if (event?.range === 'start' && startInput) {
          endInput.focus();
        } else if (event?.range === 'end' && endInput) {
          startInput.focus();
        }
      }
    }
  }, []);

  return (
      !_isEmpty(customLabel) ? (
        <div className='c-dateViewer customLabel' style={{ padding: '0 24px 0 12px', border: '0' }}>
          <span className='c-heading sm color-dark is-truncated' style={{ width: '180px', marginBottom: '5px' }}>
            {customLabel || ''}
          </span>

          <Space size={12}>
            <span className={`c-heading xs mb-0`}>{moment(confirmedDateRange[0]).format('DD/MM/YYYY')}</span>
            <Icon name='trending_flat' size='sm' isBordered />
            <span className='c-heading xs mb-0'>{moment(confirmedDateRange[1]).format('DD/MM/YYYY')}</span>
          </Space>

          <Popover
            content={renderPicker(dateRangeDuration)}
            placement={placement}
            trigger='click'
            overlayClassName='datePickerOverlay'
            overlayStyle={extraStyle ? extraStyle : {}}
            open={isPopoverVisible}
            onClose={() => setIsPopoverVisible(false)}
            onOpenChange={e => setIsPopoverVisible(e)}
          >
            <Icon name='calendar_today' isBordered size='sm' className='customLabelIcon' />
          </Popover>
        </div>
      ) : (
        <Popover
          content={renderPicker(dateRangeDuration)}
          placement={placement}
          trigger='click'
          overlayClassName='datePickerOverlay'
          overlayStyle={extraStyle ? extraStyle : {}}
          open={isPopoverVisible}
          onClose={() => setIsPopoverVisible(false)}
          onOpenChange={e => setIsPopoverVisible(e)}
          onClick={() => {
            if (!isPopoverVisible && restrictDateRange) {
              refreshAvailableDateRange();
            }
          }}
        >
          <div className='c-dateViewer'>
            <Space size={12}>
              <span className='c-heading sm mb-0 semi-bold'>
                {!_isEmpty(confirmedDateRange?.[0]) ? (
                  moment(new Date(confirmedDateRange[0])).format('DD/MM/YYYY')
                ) : (
                  <span className='c-heading sm mb-0' style={{ color: 'rgba(0, 0, 0, 0.25)' }}>
                    {t('GRAAS_START_DATE_GRAAS')}
                  </span>
                )}
              </span>
              <Icon name='trending_flat' size='sm' isBordered />
              <span className='c-heading sm mb-0 semi-bold'>
                {!_isEmpty(confirmedDateRange?.[1]) ? (
                  moment(new Date(confirmedDateRange[1])).format('DD/MM/YYYY')
                ) : (
                  <span className='c-heading sm mb-0' style={{ color: 'rgba(0, 0, 0, 0.25)' }}>
                    {t('GRAAS_END_DATE_GRAAS')}
                  </span>
                )}
              </span>
            </Space>
          </div>
        </Popover>
      )
  )
};

export default GraasDateRangePicker;

